import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import OrganizationContextSwitcher from "../organizations/ContextSwitcher";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import { GetCurrentLoggedInUser, ROLE_CLAIM } from "../account/AccountStore";
import { ListManagedServices, ScadsysManagedService, ManagedServiceType, ScadsysConfiguration } from "../managedServices/ManagedServiceStore";
import { getOrganizations } from "../organizations/OrganizationStore";
import { RenderScadsysInstance } from "../managedServices/services/Scadsys";
import { Organization } from "../users/UserStore";
import OrganizationManagedServices from "../organizations/OrganizationManagedServices";

let contextSubscriptionId = "";

interface OrgManagedServiceData {
    organization: Organization;
    managedService: ScadsysManagedService | null;
}

interface BinaryContentStatistics {
    tableName: string;
    columnName: string;
    totalRows: number;
    rowsWithContent: number;
    totalSizeBytes: number;
    totalSizeMB: number;
    errorMessage?: string;
}

interface MigrationResult {
    totalProcessed: number;
    successCount: number;
    failureCount: number;
    processedRecords: Array<{
        tableName: string;
        recordId: string;
        studentId: string;
        filePath: string;
        fileUrl: string;
        contentSize: number;
        success: boolean;
        errorMessage?: string;
    }>;
    errorMessage?: string;
}

const ScadsysIndex = () => {
    const [activeTab, setActiveTab] = React.useState<"current" | "all">("current");
    const [organizationId, setOrganizationId] = React.useState("");
    const [isAdmin, setIsAdmin] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [currentOrgManagedService, setCurrentOrgManagedService] = React.useState<ScadsysManagedService | null>(null);
    const [allOrgsData, setAllOrgsData] = React.useState<OrgManagedServiceData[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    const [binaryStats, setBinaryStats] = React.useState<BinaryContentStatistics[] | null>(null);
    const [loadingStats, setLoadingStats] = React.useState(false);
    const [migrationResult, setMigrationResult] = React.useState<MigrationResult | null>(null);
    const [isMigrating, setIsMigrating] = React.useState(false);
    const [batchSize, setBatchSize] = React.useState(10);

    React.useEffect(() => {
        // Check if user is admin
        GetCurrentLoggedInUser().then((user) => {
            const adminClaim = user.claims.find((c) => c.type === ROLE_CLAIM && (c.value === "Admin" || c.value === "SuperAdmin"));
            setIsAdmin(!!adminClaim);
        });

        contextSubscriptionId = GetContext((context) => {
            setOrganizationId(context.currentOrganization.id);
        });
        return () => {
            UnsubscribeContext(contextSubscriptionId);
        };
    }, []);

    React.useEffect(() => {
        if (activeTab === "current" && organizationId) {
            loadCurrentOrgManagedService();
            if (isAdmin) {
                loadBinaryContentStatistics();
            }
        } else if (activeTab === "all" && isAdmin) {
            loadAllOrgsManagedServices();
        }
    }, [activeTab, organizationId, isAdmin]);

    const loadCurrentOrgManagedService = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ListManagedServices(organizationId);
            // Find Scadsys managed service
            const scadsysService = response.data.find((ms) => ms.type === ManagedServiceType.Scadsys) as ScadsysManagedService | undefined;
            setCurrentOrgManagedService(scadsysService || null);
        } catch (err) {
            setError("Failed to load managed service");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadAllOrgsManagedServices = async () => {
        setLoading(true);
        setError(null);
        try {
            const orgsResponse = await getOrganizations();
            const allData = await Promise.all(
                orgsResponse.map(async (org: Organization): Promise<OrgManagedServiceData> => {
                    try {
                        const msResponse = await ListManagedServices(org.id);
                        const scadsysService = msResponse.data.find((ms) => ms.type === ManagedServiceType.Scadsys) as ScadsysManagedService | undefined;
                        return {
                            organization: org,
                            managedService: scadsysService || null,
                        };
                    } catch (err) {
                        return {
                            organization: org,
                            managedService: null,
                        };
                    }
                })
            );
            setAllOrgsData(allData);
        } catch (err) {
            setError("Failed to load organizations and managed services");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadBinaryContentStatistics = async () => {
        setLoadingStats(true);
        try {
            const response = await fetch(`/api/Scadsys/BinaryContentStatistics/${organizationId}`, {
                credentials: 'include'
            });
            const result = await response.json();
            
            if (result.success) {
                setBinaryStats(result.data);
            } else {
                console.error("Failed to load binary content statistics:", result.error);
                setBinaryStats(null);
            }
        } catch (err) {
            console.error("Error loading binary content statistics:", err);
            setBinaryStats(null);
        } finally {
            setLoadingStats(false);
        }
    };

    const startMigration = async () => {
        setIsMigrating(true);
        setMigrationResult(null);
        try {
            const response = await fetch(`/api/Scadsys/MigrateBinaryContent/${organizationId}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ batchSize })
            });
            const result = await response.json();
            
            if (result.success) {
                setMigrationResult(result.data);
                // Refresh stats after migration
                await loadBinaryContentStatistics();
            } else {
                setMigrationResult({
                    totalProcessed: 0,
                    successCount: 0,
                    failureCount: 0,
                    processedRecords: [],
                    errorMessage: result.error
                });
            }
        } catch (err) {
            console.error("Error during migration:", err);
            setMigrationResult({
                totalProcessed: 0,
                successCount: 0,
                failureCount: 0,
                processedRecords: [],
                errorMessage: err instanceof Error ? err.message : "Unknown error occurred"
            });
        } finally {
            setIsMigrating(false);
        }
    };

    const renderCurrentOrgTab = () => {
        if (loading) {
            return (
                <div className="text-center py-12">
                    <FontAwesomeIcon icon={["fad", "spinner"]} spin className="text-4xl text-blue-500 mb-4" />
                    <p className="text-gray-600">Loading managed service...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center py-12">
                    <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-4xl text-red-500 mb-4" />
                    <p className="text-red-600">{error}</p>
                </div>
            );
        }

        if (!currentOrgManagedService) {
            return (
                <div className="text-center py-12">
                    <FontAwesomeIcon icon={["fad", "server"]} className="text-6xl text-gray-300 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-700 mb-2">No Scadsys Managed Service</h2>
                    <p className="text-gray-500">This organization does not have a Scadsys managed service configured.</p>
                </div>
            );
        }

        return (
            <>
                <RenderScadsysInstance managedService={currentOrgManagedService} onChange={loadCurrentOrgManagedService} />
                
                {isAdmin && (
                    <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <FontAwesomeIcon icon={["fad", "database"]} className="mr-2 text-purple-600" />
                                Binary Content Statistics
                            </h3>
                            <button
                                onClick={loadBinaryContentStatistics}
                                disabled={loadingStats}
                                className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 transition-colors"
                            >
                                {loadingStats ? (
                                    <>
                                        <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={["fad", "sync"]} className="mr-2" />
                                        Refresh
                                    </>
                                )}
                            </button>
                        </div>
                        
                        {loadingStats && !binaryStats && (
                            <div className="text-center py-8">
                                <FontAwesomeIcon icon={["fad", "spinner"]} spin className="text-3xl text-blue-500 mb-3" />
                                <p className="text-gray-600">Loading binary content statistics...</p>
                            </div>
                        )}
                        
                        {!loadingStats && binaryStats && (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Column</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Rows</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rows with Content</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Size</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {binaryStats.map((stat, index) => (
                                            <tr key={index} className={stat.errorMessage ? "bg-red-50" : "hover:bg-gray-50"}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {stat.tableName}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {stat.columnName}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                                    {stat.errorMessage ? "—" : stat.totalRows.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                                    {stat.errorMessage ? (
                                                        "—"
                                                    ) : (
                                                        <span className={stat.rowsWithContent > 0 ? "text-green-600 font-semibold" : "text-gray-400"}>
                                                            {stat.rowsWithContent.toLocaleString()}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                                    {stat.errorMessage ? (
                                                        <span className="text-red-600 text-xs">{stat.errorMessage}</span>
                                                    ) : (
                                                        <>
                                                            <div className="font-semibold text-gray-900">{stat.totalSizeMB} MB</div>
                                                            <div className="text-xs text-gray-500">{stat.totalSizeBytes.toLocaleString()} bytes</div>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="bg-gray-100 font-semibold">
                                            <td colSpan={2} className="px-6 py-4 text-sm text-gray-900">Total</td>
                                            <td className="px-6 py-4 text-sm text-gray-900 text-right">
                                                {binaryStats.reduce((sum, stat) => sum + (stat.errorMessage ? 0 : stat.totalRows), 0).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-green-600 text-right">
                                                {binaryStats.reduce((sum, stat) => sum + (stat.errorMessage ? 0 : stat.rowsWithContent), 0).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 text-right">
                                                <div>{binaryStats.reduce((sum, stat) => sum + (stat.errorMessage ? 0 : stat.totalSizeMB), 0).toFixed(2)} MB</div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                        
                        {!loadingStats && !binaryStats && (
                            <div className="text-center py-8 text-gray-500">
                                <FontAwesomeIcon icon={["fad", "info-circle"]} className="text-3xl mb-3" />
                                <p>Click refresh to load binary content statistics</p>
                            </div>
                        )}|
                    </div>
                )}
                
                {isAdmin && currentOrgManagedService && (currentOrgManagedService.configuration as ScadsysConfiguration).storageAccountName && (
                    <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <FontAwesomeIcon icon={["fad", "cloud-upload"]} className="mr-2 text-blue-600" />
                                Binary Content Migration
                            </h3>
                        </div>
                        
                        <p className="text-gray-600 mb-4">
                            Migrate binary content from database to Azure Data Lake Storage. This process will upload files and update database references.
                        </p>
                        
                        <div className="flex items-center space-x-4 mb-4">
                            <div className="flex items-center">
                                <label htmlFor="batchSize" className="text-sm font-medium text-gray-700 mr-2">
                                    Batch Size:
                                </label>
                                <input
                                    id="batchSize"
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={batchSize}
                                    onChange={(e) => setBatchSize(parseInt(e.target.value) || 10)}
                                    disabled={isMigrating}
                                    className="w-20 px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                                />
                            </div>
                            <button
                                onClick={startMigration}
                                disabled={isMigrating || !binaryStats || binaryStats.every(s => s.rowsWithContent === 0)}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center"
                            >
                                {isMigrating ? (
                                    <>
                                        <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                        Migrating...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={["fad", "play"]} className="mr-2" />
                                        Start Migration
                                    </>
                                )}
                            </button>
                        </div>
                        
                        {migrationResult && (
                            <div className={`mt-4 p-4 rounded-lg ${
                                migrationResult.errorMessage ? 'bg-red-50 border border-red-200' : 
                                migrationResult.failureCount > 0 ? 'bg-yellow-50 border border-yellow-200' :
                                'bg-green-50 border border-green-200'
                            }`}>
                                <div className="flex items-start">
                                    <FontAwesomeIcon 
                                        icon={["fad", migrationResult.errorMessage ? "exclamation-circle" : migrationResult.failureCount > 0 ? "exclamation-triangle" : "check-circle"]} 
                                        className={`text-2xl mr-3 ${
                                            migrationResult.errorMessage ? 'text-red-600' :
                                            migrationResult.failureCount > 0 ? 'text-yellow-600' :
                                            'text-green-600'
                                        }`}
                                    />
                                    <div className="flex-1">
                                        <h4 className="font-semibold mb-2">
                                            {migrationResult.errorMessage ? 'Migration Failed' : 'Migration Complete'}
                                        </h4>
                                        {migrationResult.errorMessage ? (
                                            <p className="text-sm text-red-700">{migrationResult.errorMessage}</p>
                                        ) : (
                                            <div className="text-sm space-y-1">
                                                <p><span className="font-medium">Total Processed:</span> {migrationResult.totalProcessed}</p>
                                                <p className="text-green-700"><span className="font-medium">Successful:</span> {migrationResult.successCount}</p>
                                                {migrationResult.failureCount > 0 && (
                                                    <p className="text-red-700"><span className="font-medium">Failed:</span> {migrationResult.failureCount}</p>
                                                )}
                                            </div>
                                        )}
                                        
                                        {migrationResult.processedRecords && migrationResult.processedRecords.length > 0 && (
                                            <details className="mt-3">
                                                <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700">
                                                    View Details ({migrationResult.processedRecords.length} records)
                                                </summary>
                                                <div className="mt-2 max-h-60 overflow-y-auto">
                                                    <table className="min-w-full text-xs">
                                                        <thead className="bg-gray-100 sticky top-0">
                                                            <tr>
                                                                <th className="px-2 py-1 text-left">Table</th>
                                                                <th className="px-2 py-1 text-left">Record ID</th>
                                                                <th className="px-2 py-1 text-left">Student ID</th>
                                                                <th className="px-2 py-1 text-left">Status</th>
                                                                <th className="px-2 py-1 text-left">Size</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {migrationResult.processedRecords.map((record, idx) => (
                                                                <tr key={idx} className={record.success ? '' : 'bg-red-50'}>
                                                                    <td className="px-2 py-1">{record.tableName}</td>
                                                                    <td className="px-2 py-1">{record.recordId}</td>
                                                                    <td className="px-2 py-1">{record.studentId}</td>
                                                                    <td className="px-2 py-1">
                                                                        {record.success ? (
                                                                            <span className="text-green-600">✓ Success</span>
                                                                        ) : (
                                                                            <span className="text-red-600" title={record.errorMessage}>✗ Failed</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 py-1">{(record.contentSize / 1024).toFixed(1)} KB</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </details>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </>
        );
    };

    const renderAllOrgsTab = () => {
        if (!isAdmin) {
            return (
                <div className="text-center py-12">
                    <FontAwesomeIcon icon={["fad", "lock"]} className="text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-600">Admin access required</p>
                </div>
            );
        }

        if (loading) {
            return (
                <div className="text-center py-12">
                    <FontAwesomeIcon icon={["fad", "spinner"]} spin className="text-4xl text-blue-500 mb-4" />
                    <p className="text-gray-600">Loading all organizations...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center py-12">
                    <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-4xl text-red-500 mb-4" />
                    <p className="text-red-600">{error}</p>
                </div>
            );
        }

        return (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Organization
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Institution Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Scadsys Status
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Data Lake Setup
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {allOrgsData.map((item, index) => {
                            // Simple check for data lake setup without API calls
                            const config: ScadsysConfiguration | undefined = item.managedService?.configuration;

                            const schoolName = config?.institutionName || "—";
                            const hasStorageAccount = config?.storageAccountName;
                            const hasServicePrincipal = config?.servicePrincipalAppId;
                            const hasFileSystem = config?.fileSystemName;
                            const hasOrgFolder = config?.organizationFolder;

                            const dataLakeConfigCount = [hasStorageAccount, hasServicePrincipal, hasFileSystem, hasOrgFolder].filter(Boolean).length;
                            const dataLakePercentage = Math.round((dataLakeConfigCount / 4) * 100);

                            let dataLakeStatus = "Not Configured";
                            let dataLakeColor = "gray";
                            if (dataLakePercentage === 100) {
                                dataLakeStatus = "Fully Configured";
                                dataLakeColor = "green";
                            } else if (dataLakePercentage > 0) {
                                dataLakeStatus = `${dataLakePercentage}% Configured`;
                                dataLakeColor = "yellow";
                            }

                            return (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{item.organization.name}</div>
                                                <div className="text-sm text-gray-500">{item.organization.id}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{schoolName}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {item.managedService ? (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Configured</span>
                                        ) : (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Not Configured</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {item.managedService ? (
                                            <div className="flex flex-col gap-1">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${dataLakeColor}-100 text-${dataLakeColor}-800`}>
                                                    {dataLakeStatus}
                                                </span>
                                                {dataLakePercentage > 0 && dataLakePercentage < 100 && (
                                                    <div className="flex gap-1 text-xs text-gray-500">
                                                        {hasStorageAccount && <FontAwesomeIcon icon={["fad", "database"]} className="text-green-500" title="Storage Account" />}
                                                        {hasFileSystem && <FontAwesomeIcon icon={["fad", "folder"]} className="text-green-500" title="File System" />}
                                                        {hasOrgFolder && <FontAwesomeIcon icon={["fad", "folder-tree"]} className="text-green-500" title="Organization Folder" />}
                                                        {hasServicePrincipal && <FontAwesomeIcon icon={["fad", "key"]} className="text-green-500" title="Service Principal" />}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">N/A</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div>
            <OrganizationContextSwitcher></OrganizationContextSwitcher>

            <div className="p-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                        <FontAwesomeIcon icon={["fad", "server"]} className="mr-3 text-blue-600" />
                        Scadsys
                    </h1>
                    <p className="text-gray-600 mt-2">Manage Scadsys configurations and settings</p>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    {/* Tab Navigation */}
                    <div className="flex border-b border-gray-200 mb-6">
                        <button
                            type="button"
                            className={`px-4 py-2 text-sm font-medium border-b-2 ${
                                activeTab === "current" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                            onClick={() => setActiveTab("current")}
                        >
                            <FontAwesomeIcon icon={["fad", "building"]} className="mr-2" />
                            Current Organization
                        </button>
                        {isAdmin && (
                            <button
                                type="button"
                                className={`px-4 py-2 text-sm font-medium border-b-2 ml-8 ${
                                    activeTab === "all" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                                onClick={() => setActiveTab("all")}
                            >
                                <FontAwesomeIcon icon={["fad", "globe"]} className="mr-2" />
                                All Organizations
                            </button>
                        )}
                    </div>

                    {/* Tab Content */}
                    {activeTab === "current" && renderCurrentOrgTab()}
                    {activeTab === "all" && renderAllOrgsTab()}
                </div>
            </div>
        </div>
    );
};

export default ScadsysIndex;
